/* Sindhi Cart Drawer — controller
 * ──────────────────────────────────────────────────────────────────────────
 * Custom element <sindhi-cart-drawer>. Encapsulates open/close, save-for-later,
 * qty/variant changes via Shopify cart Ajax API, share (Web Share + clipboard
 * fallback), coupon apply (cart attribute), cause toggle, tab switching,
 * cross-sell variant chip selection, add-to-cart from rails, AI recommendations
 * tab, and bundle discount auto-detection + application.
 *
 * No external dependencies. Plays nicely with Horizon's existing cart events.
 */

(() => {
  if (customElements.get('sindhi-cart-drawer')) return;

  // Shared with sections/sindhi-location-bar.liquid — tracks real user
  // interaction so postal-code inputs can tell an auto-focus from a real tap.
  if (!window.__sindhiInteractionTracked) {
    window.__sindhiInteractionTracked = true;
    const markInteracted = () => { window.__sindhiUserInteracted = true; };
    document.addEventListener('touchstart', markInteracted, { once: true, passive: true });
    document.addEventListener('mousedown', markInteracted, { once: true });
    document.addEventListener('keydown', markInteracted, { once: true });
  }

  const ROUTES = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const url = (path) => `${ROUTES.replace(/\/$/, '')}${path}`;
  /* Delegates to sindhi-cards.js's formatMoney (wraps Shopify.formatMoney,
     honouring the shop's money-format theme setting) instead of a second,
     independent Intl.NumberFormat implementation — the two diverge whenever
     that setting changes. sindhi-cards.js isn't loaded on every template
     this drawer can appear on, hence the fallback. */
  const money = (cents) => {
    if (window.SindhiCards && window.SindhiCards.formatMoney) {
      return window.SindhiCards.formatMoney(cents || 0);
    }
    return `₹${Math.round((cents || 0) / 100).toLocaleString('en-IN')}`;
  };
  const moneyFromStr = (priceStr) => money(Math.round((parseFloat(priceStr) || 0) * 100));
  const fetchJSON = (path, opts = {}) =>
    fetch(url(path), {
      ...opts,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(opts.headers || {}) },
    }).then((r) => {
      if (!r.ok) throw new Error('fetch_failed');
      return r.json();
    });

  /* ── HTML-escape helper — use whenever inserting dynamic strings into innerHTML ── */
  const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  /* ── AI recommendation cache (sessionStorage, 10-min TTL) ─────────────── */
  const AI_TTL = 10 * 60 * 1000;
  const aiCacheKey = (productId, limit) => `sc:ai:${productId}:${limit}`;
  function aiCacheGet(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts > AI_TTL) { sessionStorage.removeItem(key); return null; }
      return data;
    } catch (e) { return null; }
  }
  function aiCacheSet(key, data) {
    try { sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch (e) {}
  }

  /* ── Bundle helpers ───────────────────────────────────────────────────── */
  function loadBundles() {
    try {
      const el = document.getElementById('sindhi-bundles');
      const bundles = el ? JSON.parse(el.textContent || '[]') : [];
      // Sort priority desc, ascending-handle tie-break — matches
      // assets/sindhi-bundle-match.js's matchForCart comparator exactly, so
      // every consumer of this island picks the same highest-priority bundle
      // as its "first" match, not just matchForCart-based ones.
      bundles.sort((a, b) => {
        const pa = a.priority || 0, pb = b.priority || 0;
        if (pa !== pb) return pb - pa;
        return (a.handle || '') < (b.handle || '') ? -1 : 1;
      });
      return bundles;
    } catch (e) { return []; }
  }
  // Estimated rupee (paise) value of a matched bundle's discount, scoped to
  // just that bundle's own products' current line total in cart -- matching
  // how the real Shopify discount is itself scoped (product-specific, not
  // cart-wide). This is a theme-side estimate for display only (the price-
  // details panel), independent of whatever Fastrr's own separately-
  // configured Bundle Discount actually charges at checkout (see
  // docs/METAFIELDS.md §5.1 -- Fastrr does not read this attribute
  // automatically, a matching rule must exist in its own dashboard too).
  function bundleDiscountAmount(matched, cart) {
    const ids = new Set((matched.product_ids || []).map(Number));
    const base = (cart.items || [])
      .filter((i) => ids.has(Number(i.product_id)))
      .reduce((sum, i) => sum + i.line_price, 0);
    const value = Number(matched.discount_value) || 0;
    if (matched.discount_type === 'percent') return Math.round((base * value) / 100);
    return Math.round(value * 100); // fixed: discount_value is rupees, cart money is paise
  }
  class SindhiCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.couponKey = this.dataset.couponKey || 'discount_code';
      this.savedKey = this.dataset.savedKey || '_saved_for_later';
      this.causeKey = this.dataset.causeKey || 'roundup_donation';
      this.currency = this.dataset.currency || 'INR';
      this.discounts = window.SindhiDiscountState && window.SindhiDiscountState.create({
        couponKey: this.couponKey,
        cartPort: {
          fetchJSON,
          getCart: () => this._getCart(),
        },
      });
      this._bound = false;
      this._lastAddedProductId = null;
      this._qtyPending = {}; /* lineKey → quantity the stepper last targeted, ahead of the DOM/server catching up */
    }

    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this._neutralizeHorizonCart(); // initial purge + creates _horizonObserver (open/close manage lifecycle)
      this._bindOpenClose();
      this._bindBag();
      this._bindCoupon();
      this._bindCause();
      this._bindGiftMessage();
      this._bindShare();
      this._bindXSell();
      this._bindAddToCart();
      this._bindBumpVariant();
      this._bindPinRow();
      this._listenGlobalAdd();
      this._listenLocation();
      this._bindEditorEvents();
      // Close drawer on navigation so the MutationObserver is disconnected and the
      // body scroll-lock is cleared before the page unloads/enters bfcache.
      window.addEventListener('pagehide', () => { if (this.classList.contains('is-open')) this.close(); });
      // If the page is restored from bfcache while the drawer is still flagged as open
      // (scroll-lock already cleared by pagehide above), remove the stale is-open class.
      window.addEventListener('pageshow', (e) => { if (e.persisted && this.classList.contains('is-open')) this.close(); });
      if (this.hasAttribute('data-auto-open') && sessionStorage.getItem('sc:open-on-load') === '1') {
        sessionStorage.removeItem('sc:open-on-load');
        this.open();
      }
      this._checkBundles().catch(() => {});
    }

    _q(sel, root = this) { return root.querySelector(sel); }
    _qa(sel, root = this) { return Array.from(root.querySelectorAll(sel)); }

    open() {
      if (this.classList.contains('is-open')) return;
      this._lastFocused = document.activeElement;
      if (this.parentElement !== document.body) {
        try { document.body.appendChild(this); } catch (e) {}
      }
      if (this._horizonObserver) {
        this._horizonObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
        this._horizonObserving = true;
      }
      const _clearInert = () => {
        document.body.removeAttribute('inert');
        document.documentElement.removeAttribute('inert');
      };
      _clearInert();
      Promise.resolve().then(_clearInert);

      this.classList.add('is-open');

      // ── iOS scroll-lock (position:fixed pattern) ──────────────────────────
      // overflow:hidden on <body> is the WRONG approach for iOS Safari: it
      // blocks all touch-scroll including inside position:fixed children
      // (our .sc-body can't be swiped even though it has overflow-y:scroll).
      // The correct pattern: save scroll position, lock with position:fixed +
      // negative top offset (keeps page visually in place), then restore on
      // close. Desktop ignores position:fixed on body gracefully.
      this._scrollLockY = window.scrollY;
      Object.assign(document.body.style, {
        overflow: 'hidden',
        position: 'fixed',
        top:   `-${this._scrollLockY}px`,
        left:  '0',
        right: '0',
      });

      const drawer = this._q('.sc-drawer');
      if (drawer) {
        if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
        drawer.focus();
      }
      this.dispatchEvent(new CustomEvent('sindhi:cart:open', { bubbles: true }));
      this._loadAITab();
    }
    close() {
      this.classList.remove('is-open');

      // Restore body scroll — reverse the position:fixed lock applied in open().
      const savedY = this._scrollLockY || 0;
      Object.assign(document.body.style, {
        overflow:  '',
        position:  '',
        top:       '',
        left:      '',
        right:     '',
      });
      window.scrollTo(0, savedY);
      this._scrollLockY = 0;

      document.body.removeAttribute('inert');
      document.documentElement.removeAttribute('inert');
      if (this._horizonObserver) { this._horizonObserver.disconnect(); this._horizonObserving = false; }
      if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
        try { this._lastFocused.focus(); } catch (e) {}
      }
      this._lastFocused = null;
      this.dispatchEvent(new CustomEvent('sindhi:cart:close', { bubbles: true }));
    }

    // Visible, focusable elements inside the open drawer panel (for the focus trap).
    _focusables() {
      const drawer = this._q('.sc-drawer');
      if (!drawer) return [];
      const sel = 'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return this._qa(sel, drawer).filter((el) => el.getClientRects().length > 0 && el.getAttribute('aria-hidden') !== 'true');
    }

    // Keep Tab / Shift+Tab cycling within the drawer while it is open.
    _trapTab(e) {
      const f = this._focusables();
      const drawer = this._q('.sc-drawer');
      if (!f.length) { e.preventDefault(); if (drawer) drawer.focus(); return; }
      const first = f[0];
      const last = f[f.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === drawer || !this.contains(active)) { e.preventDefault(); last.focus(); }
      } else if (active === last || !this.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }

    _bindOpenClose() {
      this._qa('[data-sc-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
      // Event delegation — avoids empty NodeList if children not yet parsed in connectedCallback
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-sc-close]')) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
          return;
        }
        // Backdrop click: component covers full screen; close if clicking outside the drawer panel
        if (this.classList.contains('is-open') && !e.target.closest('.sc-drawer')) {
          this.close();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.key === 'Escape') { this.close(); return; }
        if (e.key === 'Tab') this._trapTab(e);
      });
      const hijackSelectors = [
        '[data-testid="cart-drawer-trigger"]',
        'cart-drawer-component button[ref="trigger"]',
        '.cart-bubble',
        '[data-sindhi-cart-open]'
      ].join(',');
      document.addEventListener('click', (e) => {
        if (this.classList.contains('is-open')) return;
        if (this.contains(e.target)) return;
        const trig = e.target.closest(hijackSelectors);
        if (!trig) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.open();
      }, true);
    }

    async _refresh() {
      // Mutex: prevent overlapping refreshes when multiple cart actions fire quickly.
      // A call that arrived while one was already in flight used to be dropped
      // outright — if that dropped call carried the truly latest state (e.g. a
      // second rapid qty click), the drawer would render this section fetch's
      // now-stale HTML and just sit there wrong until some unrelated event
      // happened to call _refresh() again. Recorded as "pending" instead, and
      // re-run once immediately after the in-flight pass finishes, so the
      // drawer always converges to the latest state on its own rather than
      // depending on a lucky later trigger.
      if (this._refreshing) { this._refreshPending = true; return; }
      this._refreshing = true;
      this._cartCache = null; // invalidate per-cycle cart cache before re-fetching
      const html = await fetch(window.location.pathname + '?section_id=' + this._sectionId(), { headers: { Accept: 'text/html' } }).then((r) => r.text()).catch(() => null);
      if (!html) { this._refreshing = false; window.location.reload(); return; }
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('sindhi-cart-drawer');
        if (fresh) {
          const body = this._q('.sc-body');
          const footer = this._q('.sc-footer');
          const freshBody = fresh.querySelector('.sc-body');
          const freshFooter = fresh.querySelector('.sc-footer');
          /* body.replaceWith() below discards the outgoing element's
             scrollTop, which otherwise jumps a long cart back to the top on
             every coupon apply / qty change (reported by Sanjay 2026-07-28).
             Capture it first and restore on the new node inside a rAF, once
             layout has settled — restoring synchronously (pre-layout) would
             clamp against the old, not-yet-replaced scrollHeight. Clamped to
             the fresh content's own scrollHeight so a shorter cart (e.g.
             after a removal) doesn't try to scroll past its new end. */
          const savedScrollTop = body ? body.scrollTop : 0;
          const openBundles = body
            ? Array.from(body.querySelectorAll('[data-sc-bundle-details][open]')).map((d) => d.dataset.lineKey)
            : [];
          if (body && freshBody) body.replaceWith(freshBody);
          if (footer && freshFooter) footer.replaceWith(freshFooter);
          else if (freshFooter && !footer) this.querySelector('.sc-drawer').appendChild(freshFooter);
          else if (footer && !freshFooter) footer.remove();
          if (freshBody && (savedScrollTop || openBundles.length)) {
            requestAnimationFrame(() => {
              if (savedScrollTop) freshBody.scrollTop = Math.min(savedScrollTop, freshBody.scrollHeight);
              openBundles.forEach((k) => {
                const d = freshBody.querySelector(`[data-sc-bundle-details][data-line-key="${k}"]`);
                if (d) d.open = true;
              });
            });
          }
          this._bindBag();
          this._bindCoupon();
          this._bindCause();
          this._bindGiftMessage();
          this._bindXSell();
          this._bindAddToCart();
          this._bindBumpVariant();
          this._bindPinRow();
          this._updateCount();
          this._loadAITab();
          this._checkBundles();
        }
      } catch (e) { window.location.reload(); }
      finally {
        this._refreshing = false;
        if (this._refreshPending) {
          this._refreshPending = false;
          this._refresh();
          return;
        }
      }
      /* After the mutex is released (so the _refresh it may trigger isn't
         swallowed): drop a coupon that no longer applies to the cart's
         current contents, or refresh its stale amount. No-ops unless a
         code is applied and the contents actually changed. */
      this._revalidateCoupon();
    }

    _sectionId() {
      const m = this.id && this.id.match(/^sindhi-cart-(.+)$/);
      return m ? m[1] : '';
    }

    // In-flight deduplication: multiple callers within the same refresh cycle
    // share one fetch. Result cached for 800ms then stale; invalidated by _refresh().
    async _getCart() {
      if (this._cartCache && (Date.now() - this._cartCacheTs < 800)) return this._cartCache;
      if (this._cartFetching) return this._cartFetching;
      this._cartFetching = fetchJSON('/cart.js').catch(() => null);
      const result = await this._cartFetching;
      this._cartCache = result;
      this._cartCacheTs = Date.now();
      this._cartFetching = null;
      return result;
    }

    async _updateCount() {
      const cart = await this._getCart();
      if (!cart) return;
      const c = this._q('[data-sc-count]');
      if (c) {
        c.textContent = cart.item_count;
        c.dataset.count = String(cart.item_count);
      }
      /* Keep Horizon's own header cart bubble in step. It only listens for
         a 'cart:update' event carrying detail.data.itemCount, which this
         drawer's mutations don't fire, so it otherwise drifts stale (real
         bug, reported 2026-07-26 — badge read "1" for a 2-unit cart).
         cart.item_count is the TOTAL UNIT COUNT, not the line-item count. */
      const nativeCount = document.querySelector('.cart-bubble__text-count');
      const nativeBubble = document.querySelector('.cart-bubble');
      if (nativeCount) {
        nativeCount.textContent = cart.item_count < 100 ? String(cart.item_count) : '';
        nativeCount.classList.toggle('hidden', cart.item_count === 0);
      }
      if (nativeBubble) nativeBubble.classList.toggle('visually-hidden', cart.item_count === 0);
      document.dispatchEvent(new CustomEvent('sindhi:cart:updated', { detail: { cart } }));
    }

    _bindBag() {
      this._qa('[data-sc-qty]').forEach((wrap) => {
        const key = wrap.dataset.lineKey;
        const valEl = wrap.querySelector('[data-sc-qty-val]');
        const dec = wrap.querySelector('[data-sc-qty-dec]');
        const inc = wrap.querySelector('[data-sc-qty-inc]');
        /* Each click read the DISPLAYED text as "current" — but that text only
           updates once _refresh() re-renders after the fetch settles. Two
           clicks within that window both read the same stale number and sent
           the SAME target quantity to the server twice, so a rapid burst
           could land on a lower total than the number of clicks — visibly
           "losing" increments instead of adding them. Real bug, reported by
           Sanjay. Track the value the stepper is actually targeting so the
           next click always builds on the last click's intent, not on
           whatever the DOM happens to show yet. */
        const readQty = () => {
          const pending = this._qtyPending[key];
          return pending != null ? pending : parseInt((valEl && valEl.textContent) || '0', 10);
        };
        const step = (delta) => {
          const next = Math.max(0, readQty() + delta);
          this._qtyPending[key] = next;
          if (valEl) valEl.textContent = next;
          /* Loading state while in flight — buttons stay clickable so rapid
             taps keep working (the guard in _changeQty is what makes that
             safe). _refresh() replaces this whole row on success, which
             clears the class along with the node; only a genuine (non-abort)
             failure needs it cleared explicitly here. */
          wrap.classList.add('is-syncing');
          Promise.resolve(this._changeQty(key, next)).catch(() => wrap.classList.remove('is-syncing'));
        };
        if (dec) dec.addEventListener('click', () => step(-1));
        if (inc) inc.addEventListener('click', () => step(1));
      });
      this._qa('[data-sc-variant]').forEach((sel) => {
        sel.addEventListener('change', () => this._swapVariant(sel.dataset.lineKey, sel.value));
      });
      this._qa('[data-sc-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
          // Guard double-clicks — _refresh() replaces this row's markup
          // wholesale, so no manual cleanup of these states is needed.
          if (btn.disabled) return;
          btn.disabled = true;
          btn.classList.add('is-loading');
          const row = btn.closest('.sc-item');
          if (row) row.classList.add('is-removing');
          this._changeQty(btn.dataset.lineKey, 0, true);
        });
      });
      this._qa('[data-sc-item-check]').forEach((cb) => {
        cb.addEventListener('change', () => this._toggleSaved(cb.dataset.lineId, !cb.checked));
      });
      this._qa('[data-sc-item-restore]').forEach((el) => {
        el.addEventListener('click', () => this._toggleSaved(el.dataset.lineId, false));
      });
      const clear = this._q('[data-sc-clear-all]');
      if (clear) clear.addEventListener('click', () => this._clearCart());
      const saveAll = this._q('[data-sc-save-all]');
      if (saveAll) saveAll.addEventListener('click', () => this._saveAll());
    }

    async _changeQty(key, qty, skipConfirm = false) {
      // #6-B: warn before removing (qty → 0) — show inline toast on the qty block.
      // Explicit remove (×) clicks pass skipConfirm=true — that button IS the confirmation.
      if (qty === 0 && !skipConfirm) {
        const wrap = this._q(`[data-sc-qty][data-line-key="${CSS.escape(key)}"]`);
        if (wrap) {
          let warn = wrap.querySelector('.sc-qty-warn');
          if (!warn) {
            warn = document.createElement('span');
            warn.className = 'sc-qty-warn';
            warn.setAttribute('role', 'status');
            warn.textContent = 'Last one — removing it?';
            wrap.appendChild(warn);
          }
          // If already showing the warning, confirm remove; otherwise show it first
          if (warn.classList.contains('is-visible')) {
            warn.classList.remove('is-visible');
          } else {
            warn.classList.add('is-visible');
            setTimeout(() => warn.classList.remove('is-visible'), 2800);
            return; // wait for second tap
          }
        }
      }

      /* Sanjay 2026-07-28, verbatim: "when deleting the product from the
         cart which has the coupon, first coupon should be removed and
         validated against pending cart then product should be removed."
         Clear the coupon BEFORE the line-remove round-trip so the shopper
         never briefly sees a discount figure that no longer applies to the
         cart it's about to become, then re-validate the code against the
         post-removal cart and restore it only if it still qualifies. Scoped
         to qty===0 with a coupon actually applied — the common path (no
         coupon, or a plain qty change) takes zero extra round-trips. */
      if (qty === 0) {
        const cart = await this._getCart();
        const code = (cart && cart.attributes && cart.attributes[this.couponKey]) || '';
        if (code) {
          await this._writeDiscountState({ code: '', amountCents: '' });
          this._couponSig = null;

          await fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: 0 }) });

          this._cartCache = null;
          const afterCart = await this._getCart();
          if (afterCart && afterCart.items && afterCart.items.length) {
            const verdict = await this._checkCouponWithShopify(code);
            if (verdict.applicable) {
              await this._writeDiscountState({ code, amountCents: verdict.amountCents });
              /* Seed the signature exactly as _applyCoupon does after a
                 successful application, so _revalidateCoupon (called at the
                 end of the _refresh() below) doesn't immediately re-check
                 the code this just validated. */
              this._cartCache = null;
              const freshCart = await this._getCart();
              this._couponSig = `${code}|${this._cartSignature(freshCart)}`;
            } else {
              this._toast(`"${code}" no longer applies to this cart`);
            }
          }
          // Cart is now empty, or the code was left cleared above — either
          // way there is nothing further to restore.
          this._refresh();
          return;
        }
      }

      /* Guard against a superseded stepper request's response landing after a
         newer one and re-rendering the row with a stale (lower) quantity —
         see the comment in _bindBag(). window.SindhiCartGuard aborts any
         earlier in-flight request for this line; only the latest is ever
         allowed to _refresh(). */
      try {
        await window.SindhiCartGuard.guarded('sindhi-cart-drawer', key, (signal) =>
          fetchJSON('/cart/change.js', {
            method: 'POST',
            body: JSON.stringify({ id: key, quantity: qty }),
            signal,
          })
        );
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        throw err;
      }

      this._qtyPending[key] = null;
      this._refresh();
    }

    async _swapVariant(key, newVariantId) {
      const cart = await fetchJSON('/cart.js');
      const line = cart.items.find((i) => i.key === key);
      if (!line) return;
      await fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: 0 }) });
      await fetchJSON('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id: newVariantId, quantity: line.quantity }] }) });
      this._refresh();
    }

    async _toggleSaved(lineId, save) {
      const cart = await fetchJSON('/cart.js');
      const cur = (cart.attributes && cart.attributes[this.savedKey]) || '';
      const list = cur.split(',').filter(Boolean);
      const idx = list.indexOf(String(lineId));
      if (save && idx === -1) list.push(String(lineId));
      if (!save && idx !== -1) list.splice(idx, 1);
      await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: { [this.savedKey]: list.join(',') } }) });
      this._refresh();
    }

    async _clearCart() {
      await fetchJSON('/cart/clear.js', { method: 'POST' });
      await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: { [this.savedKey]: '' } }) });
      /* Was previously bundled into the write above as just
         { [this.couponKey]: '' }, missing the companion _amount attribute
         every other clear site already includes -- same bug class as
         dcb714f, just never hit in practice since an emptied cart has
         nothing to show a stale amount against. Routed through the shared
         writer now so it can't drift again. */
      await this._writeDiscountState({ code: '', amountCents: '' });
      this._refresh();
    }

    /* Re-adding a product that is currently saved for later must return it
       to the bag rather than silently fattening the saved line. Saved items
       are still real cart lines — only an attribute marks them — so
       /cart/add.js MERGES into the saved line: its quantity rises, it stays
       flagged, and the bag looks unchanged. Reported by Sanjay 2026-07-28.
       Un-saving on add is the fix at source, and it also removes the
       duplicate-line case for this path entirely. */
    async _unsaveOnAdd(variantId) {
      if (!variantId) return;
      const cart = await fetchJSON('/cart.js');
      const cur = (cart.attributes && cart.attributes[this.savedKey]) || '';
      const list = cur.split(',').filter(Boolean);
      if (!list.length) return;
      const line = (cart.items || []).find((i) => String(i.variant_id) === String(variantId));
      if (!line) return;
      const idx = list.indexOf(String(line.id));
      if (idx === -1) return;
      list.splice(idx, 1);
      await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: { [this.savedKey]: list.join(',') } }) });
      this._refresh();
    }

    async _saveAll() {
      const cart = await fetchJSON('/cart.js');
      const ids = cart.items.map((i) => String(i.id));
      await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: { [this.savedKey]: ids.join(',') } }) });
      this._refresh();
    }

    /* ── #6-A Gift message ────────────────────────────────────────────────── */
    _bindGiftMessage() {
      const gift = this._q('[data-sc-gift]');
      if (!gift) return;
      const toggle   = gift.querySelector('[data-sc-gift-toggle]');
      const panel    = gift.querySelector('[data-sc-gift-panel]');
      const textarea = gift.querySelector('[data-sc-gift-textarea]');
      const saveBtn  = gift.querySelector('[data-sc-gift-save]');
      if (!toggle || !panel) return;

      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
        panel.classList.toggle('is-open', !open);
        if (!open && textarea) setTimeout(() => textarea.focus(), 60);
      });

      if (saveBtn && textarea) {
        saveBtn.addEventListener('click', async () => {
          const note = textarea.value.trim();
          saveBtn.disabled = true;
          try {
            await fetchJSON('/cart/update.js', {
              method: 'POST',
              body: JSON.stringify({ note }),
            });
            saveBtn.classList.add('is-saved');
            saveBtn.textContent = '✓ Saved';
            setTimeout(() => {
              saveBtn.classList.remove('is-saved');
              saveBtn.textContent = saveBtn.dataset.scOrigLabel || 'Save';
              saveBtn.disabled = false;
            }, 1800);
          } catch (e) {
            saveBtn.disabled = false;
          }
        });
        // Store original label for reset
        saveBtn.dataset.scOrigLabel = saveBtn.textContent.trim();
      }

      // Also allow Enter in textarea to submit (Shift+Enter = newline)
      if (textarea && saveBtn) {
        textarea.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveBtn.click(); }
        });
      }
    }

    _bindCoupon() {
      const form = this._q('[data-sc-coupon-form]');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const code = this._q('[data-sc-coupon-input]').value.trim();
          if (code) this._applyCoupon(code);
        });
      }
      this._qa('[data-sc-coupon-suggest]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          this._applyCoupon(btn.dataset.scCouponSuggest);
        });
      });
      const rm = this._q('[data-sc-coupon-remove]');
      if (rm) rm.addEventListener('click', () => this._applyCoupon(''));
    }

    /* Signature of the cart's contents — used to tell whether the cart has
       actually changed since a coupon was last validated, so revalidation
       only pays its network cost when it can change the answer. */
    _cartSignature(cart) {
      if (!cart || !Array.isArray(cart.items)) return '';
      return cart.items
        .map((li) => `${li.variant_id}x${li.quantity}`)
        .sort()
        .join(',');
    }

    /* Ask Shopify's real discount engine about a code against the CURRENT
       cart, then put the cart back exactly as it was. Returns
       { applicable, amountCents } where amountCents is the code's OWN
       marginal contribution (afterTotal - baselineTotal), NOT the cart's
       grand total_discount after applying it. As of 2026-07-26 the coupon
       codes and the automatic quantity-break discount ("Buy More -- Save
       More") are COMBINABLE in Shopify Admin
       (combinesWith.productDiscounts: true on both), so total_discount is
       a COMBINED figure already including the tier discount, and
       snippets/sindhi-cart-drawer-body.liquid separately accounts for the
       tier part — passing the combined figure would double-count it.
       Subtracting the baseline isolates the coupon's own value.

       If these are ever set back to non-combinable this marginal figure
       becomes wrong the other way (the code would EVICT the tier discount
       rather than stack, so the difference would understate the coupon) —
       the combinability setting and this calculation are coupled; see
       docs/METAFIELDS.md manual invariant #4. */
    async _checkCouponWithShopify(code) {
      if (this.discounts) return this.discounts.checkWithShopify(code);
      let applicable = false;
      let amountCents = 0;
      let baseline = null;
      try {
        baseline = await fetchJSON('/cart.js');
        const baselineDiscount = (baseline && baseline.total_discount) || 0;
        const result = await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ discount: code }) });
        applicable = Array.isArray(result.discount_codes) &&
          result.discount_codes.some((d) => d.code === code && d.applicable === true);
        if (applicable) amountCents = Math.max(0, (result.total_discount || 0) - baselineDiscount);
      } catch (e) {
        applicable = false;
      }
      try {
        await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ discount: '' }) });
        /* Clearing the discount code is not itself a line-item mutation, so
           an automatic Function discount that Shopify evicted while the code
           was applied is NOT re-evaluated by this call alone — the cart can
           be left with neither discount until the shopper's next real
           quantity change. Re-assert every line's current quantity (a no-op
           to the shopper) to force a recompute. */
        if (baseline && Array.isArray(baseline.items) && baseline.items.length) {
          const updates = {};
          baseline.items.forEach((li) => { updates[li.key] = li.quantity; });
          await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ updates }) });
        }
      } catch (e) { /* best-effort revert */ }
      return { applicable, amountCents };
    }

    /* Single writer for the Discount Verification cart-attribute pair
       (this.couponKey / <couponKey>_amount) plus the optional bundle-
       ownership marker. Before this existed, 9 call sites each hand-rolled
       the same attributes object — which is exactly how the bundle-match
       write path (see _checkBundles below) once wrote discount_code but
       forgot the companion _amount attribute the other 8 sites already
       included, so a matched bundle's banner claimed savings the price-
       details panel never showed (fixed in commit dcb714f, one call site
       at a time; this closes the shape that produced the bug, not just
       that one instance of it).

       Any param left undefined is omitted from the write entirely (so a
       caller can update just the amount without touching the code — see
       _revalidateCoupon's stale-amount branch). Pass '' explicitly to
       clear a field. */
    async _writeDiscountState({ code, amountCents, bundleOwnedCode } = {}) {
      if (this.discounts) return this.discounts.write({ code, amountCents, bundleOwnedCode });
      const amountKey = `${this.couponKey}_amount`;
      const attributes = {};
      if (code !== undefined) attributes[this.couponKey] = code;
      if (amountCents !== undefined) attributes[amountKey] = String(amountCents);
      if (bundleOwnedCode !== undefined) attributes['_bundle_owned_code'] = bundleOwnedCode;
      return fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes }) });
    }

    /* An applied coupon is written to a cart attribute once, at apply time.
       Nothing re-checked it afterwards, so emptying the cart or swapping in
       products the code doesn't cover left the code — and its now-stale
       rupee amount — still showing, promising a discount the shopper would
       not actually get (real bug, reported 2026-07-26: remove the qualifying
       item, add a non-qualifying one, coupon still displayed).

       Runs after every refresh, but only does real work when a code is
       applied AND the cart's contents changed since the last check, so a
       plain drawer open or an unrelated re-render costs nothing. */
    async _revalidateCoupon() {
      if (this.discounts) {
        const cart = await this._getCart();
        const result = await this.discounts.revalidate(cart);
        if (result.action === 'cleared_empty' || result.action === 'updated_amount') {
          this._refresh();
        } else if (result.action === 'cleared_invalid') {
          this._toast(`"${result.code}" no longer applies to this cart`);
          this._refresh();
        }
        return;
      }
      const amountKey = `${this.couponKey}_amount`;
      const cart = await this._getCart();
      if (!cart) return;
      const code = (cart.attributes && cart.attributes[this.couponKey]) || '';
      if (!code) { this._couponSig = null; return; }

      /* Cart emptied — drop the code outright, no need to ask Shopify. */
      if (!cart.items || !cart.items.length) {
        this._couponSig = null;
        await this._writeDiscountState({ code: '', amountCents: '' });
        this._refresh();
        return;
      }

      const sig = `${code}|${this._cartSignature(cart)}`;
      if (this._couponSig === sig) return;

      const verdict = await this._checkCouponWithShopify(code);
      this._couponSig = sig;

      if (!verdict.applicable) {
        await this._writeDiscountState({ code: '', amountCents: '' });
        this._toast(`"${code}" no longer applies to this cart`);
        this._refresh();
        return;
      }

      /* Still valid, but the cart changed — the stored rupee amount is stale. */
      const storedAmount = parseInt((cart.attributes && cart.attributes[amountKey]) || '0', 10) || 0;
      if (storedAmount !== verdict.amountCents) {
        await this._writeDiscountState({ amountCents: verdict.amountCents });
        this._refresh();
      }
    }

    async _applyCoupon(code) {
      const trimmed = (code || '').trim();

      /* Busy state on whichever control triggered this call — the apply
         button [data-sc-coupon-apply] for a real code, the remove button
         [data-sc-coupon-remove] for the '' (remove) path bound to it. Either
         may be null (_q returns null after a refresh, or the markup variant
         in use doesn't render one), so every use below is guarded. */
      const btn = trimmed ? this._q('[data-sc-coupon-apply]') : this._q('[data-sc-coupon-remove]');
      if (btn && btn.disabled) return; // already in flight — ignore the re-click
      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
      }

      try {
        if (!trimmed) {
          await this._writeDiscountState({ code: '', amountCents: '' });
          this._refresh();
          return;
        }

        await this._applyCouponCode(trimmed, code);
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
        }
      }
    }

    async _applyCouponCode(trimmed, code) {
      if (this.discounts) {
        const result = await this.discounts.apply({ code: trimmed, source: 'manual' });
        if (!result.ok) {
          this._toast(`"${code}" isn't a valid or active code`);
          return;
        }
        this._refresh();
        return;
      }
      /* Validate against Shopify's real discount engine, then immediately
         revert the native application. This confirms whether the code is
         genuinely active — and what it's actually worth on this cart —
         without changing the Shopify cart's own total. Fastrr still reads
         the coupon via the cart attribute below, exactly as before; only
         whether that attribute gets written now depends on real validation
         instead of trusting user input blindly.

         discountAmountCents is this code's OWN marginal contribution
         (afterTotal - baselineTotal), NOT the cart's grand
         total_discount after applying it. As of 2026-07-26 the coupon
         codes and the automatic quantity-break discount ("Buy More --
         Save More") are configured as COMBINABLE in Shopify Admin
         (combinesWith.productDiscounts: true on both), so
         result.total_discount is a COMBINED figure that already
         includes the tier discount. snippets/sindhi-cart-drawer-body.liquid
         separately adds cart.total_discount (the tier part) to this
         attribute when rendering "Savings on MRP" / the estimated
         total, so passing the combined figure here would double-count
         the tier discount. Subtracting the baseline isolates the
         coupon's own value.

         If these discounts are ever set back to non-combinable, this
         marginal figure becomes wrong in the other direction (applying
         the code would EVICT the tier discount rather than stack, so
         the difference would understate the coupon) — the combinability
         setting and this calculation are coupled; see docs/METAFIELDS.md
         manual invariant #4. */
      const verdict = await this._checkCouponWithShopify(trimmed);
      const applicable = verdict.applicable;
      const discountAmountCents = verdict.amountCents;

      if (!applicable) {
        this._toast(`"${code}" isn't a valid or active code`);
        return;
      }

      await this._writeDiscountState({ code: trimmed, amountCents: discountAmountCents });
      /* Seed the revalidation signature so the refresh below doesn't
         immediately re-check the code we just validated. */
      this._cartCache = null;
      const freshCart = await this._getCart();
      this._couponSig = `${trimmed}|${this._cartSignature(freshCart)}`;
      this._refresh();
    }

    _bindCause() {
      const t = this._q('[data-sc-cause-toggle]');
      if (!t) return;
      t.addEventListener('click', async () => {
        const on = t.classList.toggle('is-on');
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
        await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ attributes: { [this.causeKey]: on ? 'on' : 'off' } }) });
      });
    }

    _bindShare() {
      const btn = this._q('[data-sc-share]');
      if (!btn) return;
      btn.addEventListener('click', async () => {
        const cart = await fetchJSON('/cart.js');
        if (!cart.token) return;
        const link = `${window.location.origin}/cart/${cart.token}?key=${cart.token}`;
        const payload = { title: document.title, text: 'My basket from ' + (window.Shopify && window.Shopify.shop ? window.Shopify.shop : document.title), url: link };
        if (navigator.share) {
          try { await navigator.share(payload); return; } catch (e) { /* fall through to clipboard */ }
        }
        try { await navigator.clipboard.writeText(link); this._toast('Cart link copied to clipboard'); }
        catch (e) { this._toast('Could not copy — long-press the link to share manually'); }
      });
    }

    _toast(msg) {
      const el = document.createElement('div');
      el.textContent = msg;
      el.setAttribute('role', 'status');
      el.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#3E2723;color:#FAF6EE;padding:10px 16px;border-radius:8px;font-size:12px;z-index:10000;box-shadow:0 4px 16px rgba(0,0,0,0.2);';
      /* .sc-drawer has its own `transform` (the slide-in animation), which
         establishes a containing block for position:fixed descendants —
         appending there confines the toast to the drawer panel instead of
         floating over the whole viewport like document.body would. */
      const container = this._q('.sc-drawer') || document.body;
      container.appendChild(el);
      setTimeout(() => el.remove(), 2600);
    }

    _bindXSell() {
      this._qa('[data-sc-xs-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
          const idx = tab.dataset.scXsTab;
          this._qa('[data-sc-xs-tab]').forEach((t) => {
            const active = t.dataset.scXsTab === idx;
            t.classList.toggle('is-active', active);
            // aria-pressed replaces aria-selected (tabs use data-sc-* not ARIA roles
            // to avoid Horizon's global tab-panel component treating them as dots).
            t.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          // Use BOTH class and hidden attribute — prevents Horizon global CSS from
          // overriding display:none and causing pane content to visually overlap.
          this._qa('[data-sc-xs-pane]').forEach((p) => {
            const isActive = p.dataset.scXsPane === idx;
            p.classList.toggle('is-active', isActive);
            if (isActive) { p.removeAttribute('hidden'); }
            else { p.setAttribute('hidden', ''); }
          });
        });
      });
      this._qa('[data-sc-card]').forEach((card) => {
        const chips = Array.from(card.querySelectorAll('[data-sc-card-chip]'));
        chips.forEach((chip) => {
          chip.addEventListener('click', () => {
            if (chip.disabled) return;
            chips.forEach((c) => { c.classList.remove('is-active'); c.setAttribute('aria-checked', 'false'); });
            chip.classList.add('is-active');
            chip.setAttribute('aria-checked', 'true');
            const addBtn = card.querySelector('[data-sc-add]');
            if (addBtn) {
              addBtn.dataset.variantId = chip.dataset.variantId;
              addBtn.disabled = chip.disabled;
            }
            const priceEl = card.querySelector('[data-sc-card-price]');
            const now = priceEl && priceEl.querySelector('.sc-card__price-now');
            const was = priceEl && priceEl.querySelector('.sc-card__price-was');
            const price = parseInt(chip.dataset.price, 10);
            const compare = parseInt(chip.dataset.compare, 10);
            if (now) now.textContent = money(price, this.currency);
            if (was) {
              if (compare && compare > price) { was.textContent = money(compare, this.currency); was.style.display = ''; }
              else { was.style.display = 'none'; }
            }
          });
        });
      });
    }

    // root defaults to the whole drawer; pass a narrower element to avoid
    // re-binding buttons that already have a listener (e.g. AI rail refresh).
    _bindAddToCart(root = this) {
      this._qa('[data-sc-add]', root).forEach((btn) => {
        btn.addEventListener('click', async () => {
          if (btn.disabled) return;
          const id = btn.dataset.variantId;
          if (!id) return;
          btn.disabled = true;
          const orig = btn.textContent;
          btn.textContent = 'Adding…';
          try {
            const result = await fetchJSON('/cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }) });
            if (result.items && result.items[0] && result.items[0].product_id) {
              this._lastAddedProductId = result.items[0].product_id;
            }
            this._refresh();
            this._toast('Added to your bag');
          } catch (e) {
            this._toast('Could not add — please try again');
          } finally {
            btn.disabled = false;
            btn.textContent = orig;
          }
        });
      });
    }

    _bindBumpVariant() {
      this._qa('[data-sc-bump-variant]').forEach((sel) => {
        const bump = sel.closest('.sc-bump');
        if (!bump) return;
        const btn = bump.querySelector('[data-sc-add]');
        const priceEl = bump.querySelector('.sc-bump__price');
        let variantData = [];
        try {
          const json = bump.querySelector('.sc-bump__variants-data');
          if (json) variantData = JSON.parse(json.textContent || '[]');
        } catch (e) {}
        sel.addEventListener('change', () => {
          const id = sel.value;
          if (btn) btn.dataset.variantId = id;
          if (priceEl && variantData.length) {
            const v = variantData.find((x) => String(x.id) === id);
            if (v) priceEl.textContent = money(v.price, this.currency);
          }
        });
      });
    }

    _listenGlobalAdd() {
      document.addEventListener('product:added', (e) => {
        const detail = e.detail || {};
        const pid = detail.productId || detail.product_id || detail.id || null;
        if (pid) this._lastAddedProductId = Number(pid);
        const vid = detail.variantId || detail.id || null;
        this._unsaveOnAdd(vid);
        if (this.hasAttribute('data-auto-open')) this.open();
        this._refresh();
      });
      ['ajaxCart.afterCartLoad', 'cart:added', 'cart:updated', 'cart:update'].forEach((ev) => {
        document.addEventListener(ev, () => this._refresh());
      });
    }

    /* ── Delivery-PIN row (2026-07-06 delivery-city/COD fix; moved to the
       header 2026-07-08) ──────────────────────────────────────────────
       Binds the header PIN entry form + "Change" link. Visibility of the
       form vs. the "{city} · Change" display is pure CSS (html[data-sdp-zone]
       presence — see assets/sindhi-cart-drawer.css); this only wires actions
       and keeps [data-sc-pin-city] text in sync. _bindPinRow() is called both
       at init and after every _refresh() (line ~273) — that second call used
       to be safe when the row lived in .sc-footer, which _refresh() replaces
       wholesale each time (old listeners died with the old node). Now the
       row lives in .sc-header, which _refresh() never touches, so the same
       DOM node would otherwise accumulate one more duplicate listener per
       refresh. Guard with a bound-once flag so repeat calls are no-ops. */
    _bindPinRow() {
      const form     = this._q('[data-sc-pin-form]');
      const dialogEl = this._q('[data-sc-pin-dialog]');
      const trigger  = this._q('[data-sc-pin-trigger]');
      const closeBtn = this._q('[data-sc-pin-dialog-close]');

      // Popup form (mobile) vs. inline form (desktop/tablet) — same pattern
      // as the header location bar (sindhi-location-bar.liquid). .show()
      // opens non-modally, in normal document flow, no backdrop: matches
      // desktop/tablet's old always-visible inline row exactly. .showModal()
      // (wired to the trigger) gives mobile a proper centered popup.
      if (dialogEl && !dialogEl.dataset.scDialogBound) {
        dialogEl.dataset.scDialogBound = '1';
        // Re-checked on resize (not just at load) — a desktop→mobile resize
        // would otherwise leave the dialog open non-modally with the
        // trigger also visible underneath it.
        const applyDialogBreakpoint = () => {
          const isDesktop = window.matchMedia('(min-width: 750px)').matches;
          if (!this.offsetParent && !this.getClientRects().length) {
            if (dialogEl.open) dialogEl.close();
            return;
          }
          if (isDesktop) {
            if (!dialogEl.open && typeof dialogEl.show === 'function') dialogEl.show();
          } else if (dialogEl.open && !dialogEl.matches(':modal')) {
            dialogEl.close();
          }
        };
        applyDialogBreakpoint();
        window.addEventListener('resize', applyDialogBreakpoint);
        if (trigger && typeof dialogEl.showModal === 'function') {
          trigger.addEventListener('click', () => dialogEl.showModal());
        }
        if (closeBtn) {
          closeBtn.addEventListener('click', () => dialogEl.close());
        }
      }

      if (form && !form.dataset.scPinBound) {
        form.dataset.scPinBound = '1';
        const submitPin = (e) => {
          if (e) e.preventDefault();
          if (!window.SindhiLocation) return;
          const input     = this._q('[data-sc-pin-input]', form);
          const errorEl   = this._q('[data-sc-pin-error]');
          const submitBtn = this._q('[data-sc-pin-submit]', form);
          if (submitBtn && submitBtn.disabled) return; // already in flight
          const pin = ((input && input.value) || '').trim();
          if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
          const proxyMeta = document.querySelector('meta[name="sdp-proxy-url"]');
          const proxyUrl  = proxyMeta ? proxyMeta.content : '';
          if (submitBtn) { submitBtn.disabled = true; submitBtn.setAttribute('aria-busy', 'true'); }
          if (input) input.disabled = true;
          form.classList.add('is-loading');
          window.SindhiLocation.fetch(pin, proxyUrl, (data, err) => {
            form.classList.remove('is-loading');
            if (submitBtn) { submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }
            if (input) input.disabled = false;
            if (err) {
              if (errorEl) { errorEl.textContent = err; errorEl.hidden = false; }
              // Leave the popup open on error so the user can see the message
              // and retry immediately without re-tapping the trigger.
              return;
            }
            if (dialogEl && dialogEl.open) dialogEl.close();
            // sdp:location:set (dispatched by SindhiLocation.set) already refreshes
            // ETA/COD everywhere via sindhi-delivery-badge.js's global listeners;
            // this only updates the drawer's own "{city} · Change" label.
            this._updatePinDisplay();
          });
        };
        form.addEventListener('submit', submitPin);
        // Auto-submit once a valid 6-digit PIN is typed — no need to press
        // Check or hit Enter. Re-checked on every keystroke so edits after
        // the 6th digit (e.g. a correction) still trigger a fresh lookup.
        const autoInput = this._q('[data-sc-pin-input]', form);
        if (autoInput) {
          autoInput.addEventListener('input', () => {
            if (/^\d{6}$/.test((autoInput.value || '').trim())) submitPin(null);
          });
          // Some mobile browsers auto-focus autocomplete="postal-code" inputs
          // (e.g. when the drawer auto-opens on load) without a real tap ever
          // landing on them, popping the keyboard unprompted. If this input
          // gains focus before any real user interaction, blur it.
          if (window.matchMedia('(max-width: 749px)').matches) {
            autoInput.addEventListener('focus', () => {
              if (!window.__sindhiUserInteracted) autoInput.blur();
            }, { once: true });
          }
        }
      }
      const changeBtn = this._q('[data-sc-pin-change]');
      if (changeBtn && !changeBtn.dataset.scPinBound) {
        changeBtn.dataset.scPinBound = '1';
        changeBtn.addEventListener('click', () => {
          if (window.SindhiLocation && typeof window.SindhiLocation.clear === 'function') {
            window.SindhiLocation.clear();
          }
          const input = this._q('[data-sc-pin-input]');
          if (input) { input.value = ''; input.disabled = false; }
          const errorEl = this._q('[data-sc-pin-error]');
          if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
          const submitBtn = this._q('[data-sc-pin-submit]');
          if (submitBtn) { submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }
          const form = this._q('[data-sc-pin-form]');
          if (form) form.classList.remove('is-loading');
          this._updatePinDisplay();
          // sdp:location:clear (fired by SindhiLocation.clear()) removes
          // html[data-sdp-zone], revealing the trigger+dialog via CSS — but
          // the dialog itself was already .close()d on the earlier success,
          // so on mobile it needs re-opening for the user to actually edit.
          const dialogEl = this._q('[data-sc-pin-dialog]');
          if (dialogEl && !dialogEl.open && typeof dialogEl.showModal === 'function'
              && window.matchMedia('(max-width: 749px)').matches) {
            dialogEl.showModal();
          }
          if (input) input.focus();
        });
      }
      this._updatePinDisplay();
    }

    _updatePinDisplay() {
      const cityEl = this._q('[data-sc-pin-city]');
      if (!cityEl) return;
      const loc = window.SindhiLocation ? window.SindhiLocation.get() : null;
      cityEl.textContent = (loc && loc.pin) ? (loc.city || ('PIN ' + loc.pin)) : '';
    }

    // One-time global listener (registered once in connectedCallback, unlike
    // _bindPinRow which must be re-run after every footer re-render) so the
    // "{city} · Change" label stays in sync when location changes from
    // outside the drawer (e.g. the PDP pincode widget or location bar).
    _listenLocation() {
      ['sdp:location:set', 'sdp:location:clear', 'sdp:location:ready'].forEach((ev) => {
        document.addEventListener(ev, () => this._updatePinDisplay());
      });
    }

    /* ── AI Recommendations Tab ───────────────────────────────────────────── */

    async _loadAITab() {
      const pane = this._q('[data-sc-ai-pane]');
      if (!pane) return;
      const limit = parseInt(pane.dataset.aiLimit || '4', 10);
      const intent = this.dataset.aiTabIntent || 'complementary';
      const filterHandle = this.dataset.aiFilterCollection || '';

      /* Root-cause fix (2026-07-11): the cart fetch used to be awaited
         up front, serializing a full /cart.js round-trip in front of the
         (already slower) Shopify recommendations API call on every
         drawer open — the dominant cause of this tab's load time. It's
         only actually needed to filter already-in-cart items out of the
         results below and as a productId fallback, neither of which
         blocks starting the recommendations request. Kick it off here
         without awaiting so it resolves in parallel; total wait becomes
         max(cart, recommendations) instead of cart + recommendations. */
      const cartPromise = this._getCart();

      /* Determine which product ID to recommend against. _lastAddedProductId
         is set synchronously right before _refresh()/open() fire on every
         add-to-cart (see the /cart/add.js and 'product:added' handlers
         above), so in the common flow it's already known here and the
         cart fetch isn't needed just to start the recommendations call. */
      let productId = this._lastAddedProductId;
      if (!productId) {
        const cart = await cartPromise;
        if (cart && cart.items && cart.items.length) {
          productId = cart.items[cart.items.length - 1].product_id;
        }
      }
      if (!productId) return;

      const cacheKey = aiCacheKey(productId, limit);
      let products = aiCacheGet(cacheKey);

      if (!products) {
        /* Show the shimmer skeleton immediately for this cold fetch — covers
           not just first paint (already handled server-side in the Liquid
           template) but also later cold loads, e.g. a second, different
           product added later in the session, where the rail would
           otherwise keep showing stale cards from the previous fetch while
           this one is in flight. */
        const rail = pane.querySelector('.sc-rail');
        if (rail) rail.innerHTML = this._renderSkeletonCards(limit);
        try {
          const fetchIntent = async (iType) => {
            const resp = await fetch(`/recommendations/products.json?product_id=${productId}&intent=${iType}&limit=${limit + 5}`);
            const data = await resp.json();
            return data.products || [];
          };
          const mergeById = (a, b) => {
            const seen = {};
            return a.concat(b).filter((p) => { if (seen[p.id]) return false; seen[p.id] = true; return true; });
          };
          if (intent === 'both') {
            const [comp, rel] = await Promise.all([fetchIntent('complementary'), fetchIntent('related')]);
            products = mergeById(comp, rel);
          } else {
            products = await fetchIntent(intent);
          }
          /* Optional collection filter */
          if (filterHandle) {
            try {
              const colResp = await fetch(`/collections/${filterHandle}/products.json?limit=250`);
              const colData = await colResp.json();
              const colIds = new Set((colData.products || []).map((p) => p.id));
              products = products.filter((p) => colIds.has(p.id));
            } catch (e) {}
          }
          aiCacheSet(cacheKey, products);
        } catch (e) { return; }
      }

      if (!products.length) return;

      /* Await the cart fetch kicked off above — it started in parallel with
         the recommendations request and, since that request is the slower
         of the two, this almost always resolves instantly here rather than
         adding further wait. */
      const cart = await cartPromise;
      const cartProductIds = cart ? (cart.items || []).map((i) => i.product_id) : [];

      const filtered = products.filter((p) => !cartProductIds.includes(p.id)).slice(0, limit);
      this._updateBundleNudge(loadBundles(), cartProductIds, pane);
      if (!filtered.length) return;

      const rail = pane.querySelector('.sc-rail');
      if (rail) {
        rail.innerHTML = filtered.map((p) => this._renderAICard(p)).join('');
        // Scope to the rail only — avoids double-binding existing cross-sell / bump buttons
        // that were already bound during the preceding _refresh() call.
        this._bindAddToCart(rail);
      }
    }

    _updateBundleNudge(bundles, cartProductIds, pane) {
      if (!pane) return;
      let nudgeEl = pane.querySelector('[data-sc-bundle-nudge]');
      if (!nudgeEl) {
        nudgeEl = document.createElement('div');
        nudgeEl.setAttribute('data-sc-bundle-nudge', '');
        nudgeEl.setAttribute('role', 'status');
        nudgeEl.setAttribute('aria-live', 'polite');
        nudgeEl.className = 'sc-bundle-notification sc-bundle-nudge';
        nudgeEl.setAttribute('hidden', '');
        const rail = pane.querySelector('.sc-rail');
        pane.insertBefore(nudgeEl, rail || pane.firstChild);
      }
      const partial = bundles.find((b) => {
        if (!b.product_ids || b.product_ids.length < 2) return false;
        const inCart = b.product_ids.some((pid) => cartProductIds.includes(Number(pid)));
        const notInCart = b.product_ids.some((pid) => !cartProductIds.includes(Number(pid)));
        return inCart && notInCart;
      });
      if (!partial) { nudgeEl.hidden = true; return; }
      const remaining = partial.product_ids.filter((pid) => !cartProductIds.includes(Number(pid))).length;
      const valStr = partial.discount_type === 'percent'
        ? `${partial.discount_value}%`
        : money(partial.discount_value * 100, this.currency);
      const msg = partial.cta_label
        || `Add ${remaining} more to unlock ${valStr} off — ${partial.name || 'bundle'}`;
      nudgeEl.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M20 12V7H4v5a2 2 0 010 4v5h16v-5a2 2 0 010-4z"/><path d="M9 7v10"/></svg><span>${msg}</span>`;
      nudgeEl.removeAttribute('hidden');
    }

    _renderAICard(product) {
      const variant = product.variants && product.variants[0];
      if (!variant) return '';
      // esc() for attribute and text context; href needs attribute-safe escaping too.
      const safeTitle = esc(product.title || '');
      const rawHref   = product.url || `/products/${product.handle || ''}`;
      const safeHref  = esc(rawHref);
      const imgSrc = product.featured_image
        ? (typeof product.featured_image === 'string' ? product.featured_image : (product.featured_image.src || ''))
        : '';
      const imgEl = imgSrc
        ? `<img src="${esc(imgSrc)}" alt="${safeTitle}" width="130" height="130" loading="lazy">`
        : '';
      const priceNow = money(variant.price, this.currency);
      const compareAt = variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<span class="sc-card__price-was">${money(variant.compare_at_price, this.currency)}</span>` : '';
      const addBtn = variant.available
        ? `<button class="sc-card__add" data-sc-add data-variant-id="${variant.id}" aria-label="Add ${safeTitle} to cart">+ Add</button>`
        : `<button class="sc-card__add" disabled>Sold out</button>`;
      return `<div class="sc-card" data-sc-card>
  <a class="sc-card__media" href="${safeHref}" tabindex="-1" aria-hidden="true">${imgEl}</a>
  <a class="sc-card__title" href="${safeHref}">${safeTitle}</a>
  <p class="sc-card__price" data-sc-card-price>
    <span class="sc-card__price-now">${priceNow}</span>${compareAt}
  </p>
  ${addBtn}
</div>`;
    }

    /* Graceful loading state for the AI tab — same markup shape/dimensions
       as _renderAICard's real .sc-card so there's no layout shift when the
       fetch resolves and swaps this out. aria-hidden since it's decorative;
       the pane's "Finding the best picks…" text (server-rendered fallback,
       kept in the DOM as a visually-hidden sibling — see the Liquid
       template) carries the accessible loading announcement. */
    _renderSkeletonCards(count) {
      const n = Math.max(1, Math.min(count || 4, 6));
      const card = `<div class="sc-card sc-card--skeleton" aria-hidden="true">
  <div class="sc-card__media--skeleton"></div>
  <div class="sc-card__title--skeleton"></div>
  <div class="sc-card__price--skeleton"></div>
  <div class="sc-card__add--skeleton"></div>
</div>`;
      return new Array(n).fill(card).join('');
    }

    /* ── Bundle Discount Detection ────────────────────────────────────────── */

    /* this.couponKey (default 'discount_code') is the SAME cart attribute a
       shopper's manually-typed coupon lives in (see _applyCoupon). This runs
       on every page load (connectedCallback) and every _refresh(), so its
       "no bundle matches" branch must never clear that attribute just
       because it can't currently prove the code is bundle-related — doing
       so silently wipes a real coupon (e.g. GANESHFEST) the moment bundles
       is empty (Task 9's empty-island case, live now since the
       discount_verified backfill hasn't run). Ownership is tracked
       explicitly via the '_bundle_owned_code' cart attribute, set whenever
       WE write the coupon key, so the "not matched" branch only ever clears
       a code it can prove it applied itself. */
    async _checkBundles() {
      const notifEl = this._q('[data-sc-bundle-notification]');
      const textEl = this._q('[data-sc-bundle-text]');
      if (!notifEl || !textEl) return;

      const bundles = loadBundles();
      const cart = await this._getCart();
      if (!cart) { notifEl.hidden = true; return; }

      const cartProductIds = (cart.items || []).map((i) => Number(i.product_id));
      const matched = (bundles.length && cart.items && cart.items.length)
        ? window.SindhiBundle.matchForCart(bundles, cartProductIds)
        : null;

      const currentCode = (cart.attributes && cart.attributes[this.couponKey]) || '';
      const ownedCode = (cart.attributes && cart.attributes['_bundle_owned_code']) || '';

      if (matched) {
        /* Auto-apply the discount code as a cart attribute for Fastrr, and
           mark it as ours so a later "no longer matches" pass can safely
           clear it without guessing. Only write when there's an actual code
           to write AND the slot is free (empty) or already bundle-owned —
           never stomp a shopper's manually-applied coupon (blank
           matched.discount_code happens for Function-backed bundles with no
           code, and currentCode being a real code we don't own means the
           shopper typed it themselves — either way, hands off). */
        const canWrite = !!matched.discount_code && currentCode !== matched.discount_code
          && (!currentCode || currentCode === ownedCode);
        if (canWrite) {
          try {
            await this._writeDiscountState({
              code: matched.discount_code,
              amountCents: bundleDiscountAmount(matched, cart),
              bundleOwnedCode: matched.discount_code,
            });
          } catch (e) {}
        }
        // Only claim "{code} applied" when that's actually true: we either just
        // wrote it, it was already applied, or the bundle has no code to claim
        // (code-less/Function-backed). If a shopper's own coupon is blocking the
        // write, showing the bundle's code here would name the WRONG code as
        // applied — hide the banner instead of lying about what's active.
        const bannerAccurate = !matched.discount_code || currentCode === matched.discount_code || canWrite;
        if (bannerAccurate) {
          const valueStr = matched.discount_type === 'percent'
            ? `${matched.discount_value}%`
            : moneyFromStr(String(matched.discount_value), this.currency);
          // Build with esc() to prevent stored XSS from admin-controlled bundle fields.
          const safeCode  = esc(matched.discount_code || '');
          const safeValue = esc(valueStr);
          const safeName  = esc(matched.name || '');
          const tpl = (this.dataset.bundleAppliedText || '{code} applied — saving {value} on {name}')
            .replace('{code}', safeCode)
            .replace('{value}', safeValue)
            .replace('{name}', safeName);
          const badge = esc(this.dataset.bundleBadgeLabel || 'Bundle Discount');
          textEl.innerHTML = `<strong>${badge} ${safeCode}</strong> — ${tpl}`;
          notifEl.removeAttribute('hidden');
        } else {
          notifEl.hidden = true;
        }
      } else {
        /* No bundle applies. Only clear the coupon if we are CERTAIN we
           applied it ourselves (the owner marker matches what's currently
           applied) — never clear a manually-typed coupon just because the
           bundle list is empty or no longer matches. */
        if (currentCode && ownedCode && ownedCode === currentCode) {
          try {
            await this._writeDiscountState({ code: '', amountCents: '', bundleOwnedCode: '' });
          } catch (e) {}
        }
        notifEl.hidden = true;
      }
    }

    /* ── Horizon native cart neutralization ──────────────────────────────── */
    // Suppresses Horizon's built-in cart-drawer-component so it doesn't open
    // alongside our custom drawer. Creates this._horizonObserver (not yet started).
    // open() re-attaches the observer; close() disconnects it (performance).
    _neutralizeHorizonCart() {
      const closeAndDetach = (dialog) => {
        try { if (dialog.open) dialog.close(); } catch (e) {}
        try { dialog.removeAttribute('open'); } catch (e) {}
        dialog.style.display = 'none';
      };
      // Debounce guard: purge() runs at most once per animation frame so rapid
      // DOM mutations (e.g. Horizon re-rendering) don't cascade into a freeze.
      let _purgeScheduled = false;
      const purge = () => {
        if (_purgeScheduled) return;
        _purgeScheduled = true;
        requestAnimationFrame(() => {
          _purgeScheduled = false;
          // Disconnect before mutating: our own removals/replacements below are
          // childList + 'open'-attribute mutations — exactly what this observer
          // watches for — so without disconnecting first, purge() retriggers
          // itself every animation frame forever once open() has started
          // observing (self-feeding loop that pegs the main thread and repeatedly
          // opens/closes Horizon's native dialog). Reconnect after only if the
          // drawer is still open, so genuine Horizon re-insertions later in the
          // same open session still get caught.
          const wasObserving = this._horizonObserving;
          if (this._horizonObserver) this._horizonObserver.disconnect();
          document.querySelectorAll('cart-drawer-component dialog, dialog.cart-drawer__dialog').forEach(closeAndDetach);
          document.querySelectorAll('cart-drawer-component').forEach((comp) => {
            const innerTriggers = comp.querySelectorAll('[data-testid="cart-drawer-trigger"], button[ref="trigger"], .cart-bubble');
            innerTriggers.forEach((trig) => {
              if (trig.dataset.scNeutralized) return;
              const clone = trig.cloneNode(true);
              clone.dataset.scNeutralized = '1';
              trig.replaceWith(clone);
            });
            comp.querySelectorAll('dialog').forEach((d) => { closeAndDetach(d); d.remove(); });
            comp.querySelectorAll('template').forEach((t) => t.remove());
          });
          // Always clear any inert/overflow locks Horizon may have set on the page root.
          document.body.removeAttribute('inert');
          document.documentElement.removeAttribute('inert');
          if (wasObserving && this._horizonObserver) {
            this._horizonObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
          }
        });
      };
      purge(); // initial cleanup on connect
      // Observer created but NOT yet started — open() attaches, close() disconnects
      this._horizonObserver = new MutationObserver(() => purge());
    }

    _bindEditorEvents() {
      if (!window.Shopify || !window.Shopify.designMode) return;

      document.addEventListener('shopify:section:select', (e) => {
        if (this.id && !this.id.includes(e.detail.sectionId)) return;
        this.open();
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (this.id && !this.id.includes(e.detail.sectionId)) return;
        this.close();
      });

      document.addEventListener('shopify:block:select', (e) => {
        if (this.id && !this.id.includes(e.detail.sectionId)) return;
        this.open();
        const blocks = this._qa('[data-shopify-editor-block]');
        const target = blocks.find((el) => {
          try { return JSON.parse(el.dataset.shopifyEditorBlock || '{}').blockId === e.detail.blockId; } catch { return false; }
        });
        if (!target) return;
        const body = this._q('.sc-body');
        setTimeout(() => {
          if (body) body.scrollTop = Math.max(0, target.offsetTop - 60);
          target.style.outline = '2px solid var(--sc-accent)';
          target.style.outlineOffset = '2px';
          target.style.borderRadius = '4px';
          target.style.transition = 'outline 120ms ease';
        }, 300);
      });

      document.addEventListener('shopify:block:deselect', () => {
        this._qa('[data-shopify-editor-block]').forEach((el) => {
          el.style.outline = '';
          el.style.outlineOffset = '';
          el.style.borderRadius = '';
        });
      });
    }
  }

  customElements.define('sindhi-cart-drawer', SindhiCartDrawer);
})();
